import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { cls } from '@mssgme/helpers';
import { useAccessControl } from '@mssgme/hooks';
import { RestrictAccessTypes } from '../../../domainTypes';
import { Button, LinkButton } from '../../Inputs';
import { PortalModalSheet } from '../../ModalSheet';

import styles from './styles.scss';

const getUpdateRedirect = ({ code, plan, target }) => {
    if (code === 'forbidden') {
        return;
    }

    const baseUrl = __LEGACY__ ? '/s' : '';
    const path = [baseUrl, 'billing'];

    if (code !== 'unpaid') {
        path.push('plans');

        if (code === 'upgrade') {
            path.push(target);
        }
        // todo: plan quantity is not used for now, uncomment the following if it's changed
        // else if (code === 'upgradeTeamLimit') {
        //     path.push(plan);
        //     path.push('quantity');
        // }
    }

    return path.join('/');
};

const permissionToTranslationKey = (permission) => permission ? permission.replace(':', '.') : '';

/**
 * @param {string} className
 * @param {*} [children]
 * @param {*} [referrer]
 * @param {import('@mssgme/shared').AccessRequestError} reason
 * @param {boolean} [center]
 * @param {Function} onCancel
 * @param modal
 * @param rest
 * @returns {JSX.Element}
 */
export function NoAccess({ className, children, referrer, reason, center = false, onCancel, modal, ...rest }) {
    const { t } = useTranslation('ui');
    const { isAdmin } = useAccessControl();
    const buttonTo = useMemo(() => (isAdmin && getUpdateRedirect(reason)), [isAdmin, reason]);
    const title = t(`NoAccess.${reason.code}.heading`, reason);
    const description =
        t(`NoAccess.${reason.code}.features.${permissionToTranslationKey(reason.permission)}`, '', reason) ||
        t(`NoAccess.${reason.code}.description`, reason);

    const content = (
        <>
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.text}>{description}</p>
            {referrer && (
                <LinkButton fullWidth btnStyle="secondary2" className={styles.spaced} to={referrer}>
                    {t('NoAccess.cancel')}
                </LinkButton>
            )}
            {onCancel && (
                <Button fullWidth btnStyle="secondary2" className={styles.spaced} onClick={onCancel}>
                    {t('NoAccess.cancel')}
                </Button>
            )}
            {buttonTo && (
                <LinkButton
                    fullWidth
                    btnStyle="primary"
                    to={buttonTo}
                    onClick={onCancel}
                    data-button={`upgrade-to-${reason.target}`}
                    data-event="click-upgrade"
                    data-variant="restrict"
                >
                    {t(`NoAccess.${reason.code}.button`, reason)}
                </LinkButton>
            )}
            {children}
        </>
    );

    const Container = modal ? PortalModalSheet : 'div';

    return (
        <Container {...cls([styles.root, center && styles.center, className], rest)}>{content}</Container>
    );
}

NoAccess.propTypes = {
    className: PropTypes.string,
    reason: RestrictAccessTypes.reason.isRequired,
    onCancel: PropTypes.func,
    referrer: PropTypes.string,
    center: PropTypes.bool,
    children: PropTypes.any,
    modal: PropTypes.bool,
};
